/**
 * 사이드바 (데스크탑 오른쪽 — 네비게이션 전용)
 *
 * 캠페인 일정 요약과 섹션 네비게이션 링크를 보여주는 sticky 사이드바.
 * 데스크탑에서만 표시되며, 모바일에서는 MobileInfoSection이 대신한다.
 */

import type { PublicCampaignResponse } from '@/lib/seedingPublicApi'
import { formatCompactDate, formatShortDate, getCountdown } from './utils'

/** 섹션 ID 타입 */
export type SectionId = 'product' | 'detail' | 'guide' | 'apply'

interface SidebarSectionProps {
  campaign: PublicCampaignResponse
  activeSection: SectionId
  onNavigate: (sectionId: SectionId) => void
}

export function SidebarSection({
  campaign,
  activeSection,
  onNavigate,
}: SidebarSectionProps) {
  const countdown = getCountdown(campaign)

  // 네비게이션 항목 (조건부 표시, 신청하기는 별도 버튼으로 렌더링)
  const navItems: { id: SectionId; label: string }[] = [
    { id: 'product', label: '제공 내역' },
    { id: 'detail', label: '캠페인 소개' },
    // 미션 안내: mission_rounds가 있을 때 표시
    ...(campaign.mission_rounds?.length
      ? [{ id: 'guide' as SectionId, label: '미션 안내' }]
      : []),
  ]

  return (
    <div className="sticky top-8 pt-6">
      {/* 일정 정보 (레뷰 스타일: 라벨 + 값 왼쪽 정렬) */}
      <div className="space-y-2.5 text-sm mb-6">
        <div className="flex gap-4">
          <span className="text-on-surface-variant w-28 shrink-0">캠페인 신청기간</span>
          <div>
            <span className="font-medium">
              {formatCompactDate(campaign.recruit_start_at)} ~ {formatCompactDate(campaign.recruit_end_at)}
            </span>
            {countdown && (
              <p className="text-xs text-error font-semibold mt-0.5">{countdown}</p>
            )}
          </div>
        </div>

        {campaign.selection_announce_at && (
          <div className="flex gap-4">
            <span className="text-on-surface-variant w-28 shrink-0">선정 발표</span>
            <span className="font-medium">{formatCompactDate(campaign.selection_announce_at)}</span>
          </div>
        )}

        {/* 미션 일정 */}
        {campaign.mission_rounds && campaign.mission_rounds.length > 0 ? (
          <div className="flex gap-4">
            <span className="text-on-surface-variant w-28 shrink-0">미션 일정</span>
            <div>
              {campaign.mission_rounds.map((round) => (
                <p key={round.round} className="font-medium">
                  {round.round}차: ~{formatShortDate(round.deadline_at)}
                  {round.title && (
                    <span className="text-on-surface-variant ml-1">({round.title})</span>
                  )}
                </p>
              ))}
            </div>
          </div>
        ) : campaign.mission_deadline_at ? (
          <div className="flex gap-4">
            <span className="text-on-surface-variant w-28 shrink-0">미션 마감</span>
            <span className="font-medium">{formatCompactDate(campaign.mission_deadline_at)}</span>
          </div>
        ) : null}

        <div className="flex gap-4">
          <span className="text-on-surface-variant w-28 shrink-0">신청자</span>
          <span>
            <span className="text-primary font-bold">{campaign.application_count}</span>
            {' '}/ {campaign.recruit_count}명
          </span>
        </div>
      </div>

      {/* 구분선 */}
      <div className="border-t border-border mb-2" />

      {/* 네비게이션 링크 */}
      <nav>
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onNavigate(item.id)}
            className={`block w-full text-left py-3 border-b border-border text-base transition-colors
              ${activeSection === item.id
                ? 'font-bold text-on-surface'
                : 'text-on-surface-variant hover:text-on-surface'}`}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {/* 신청하기 버튼 (모집중일 때만) */}
      {campaign.status === 'recruiting' && (
        <button
          type="button"
          onClick={() => onNavigate('apply')}
          className="block w-full text-center py-3 text-lg font-bold text-primary hover:text-primary transition-colors"
        >
          신청하기 &rarr;
        </button>
      )}
    </div>
  )
}
